import React from "react";
import { Autocomplete, Grid, TextField, Typography } from "@mui/material";
import { EnforcementNoticeSearchState } from "../../pages/search/EnforcementNotice/EnforcementNoticeSearchState";
import EnforcementNoticeSimpleSearch from "../../pages/search/EnforcementNotice/EnforcementNoticeSimpleSearch";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { SearchHelper } from "../../pages/search/SearchHelper";
import "../../pages/search/Search.css";
import { NoticeStatus } from "@shared/mirage/core/enums";
import { EnforcementNoticeFilter } from "@shared/publicportal/contracts/EnforcementNoticeFilter";
import datePickerValueFormatter from "@mirage/components/helpers/datePickerValueFormatter";

interface EnforcementNoticeAdvSearchProps {
    state: EnforcementNoticeSearchState;
    setter: (modifier: Partial<EnforcementNoticeFilter>) => void;
}

const EnforcementNoticeAdvancedSearch: React.FC<EnforcementNoticeAdvSearchProps> = (props) => {
    return (
        <>
            <Grid container className="search-container" sx={{ width: { sm: "100%", md: "80%", lg: "60%", xl: "70%" } }}>
                <EnforcementNoticeSimpleSearch setter={props.setter} state={props.state} />
            </Grid>
            <Grid container className="search-container" sx={{ width: { sm: "100%", md: "80%", lg: "60%", xl: "70%" } }}>
                <Grid size={{ xs: 12, md: 12, xl: 12 }}>
                    <Typography variant="h5">Details</Typography>
                </Grid>
                {getDetailFields().map((field) => {
                    return (
                        <Grid size={{ xs: 12, md: 6, xl: 4 }} key={"e-notice-details-" + field.key}>
                            {field}
                        </Grid>
                    );
                })}
            </Grid>
            <Grid container className="search-container" sx={{ width: { sm: "100%", md: "80%", lg: "60%", xl: "45%" } }}>
                <Grid size={{ xs: 12, md: 12, xl: 12 }}>
                    <Typography variant="h5">Date Fields</Typography>
                </Grid>
                {getDateFields().map((field) => {
                    return (
                        <Grid size={{ xs: 12, md: 6, xl: 6 }} key={"e-notice-dates-" + field.key}>
                            {field}
                        </Grid>
                    );
                })}
            </Grid>
        </>
    );

    function getDetailFields() {
        return [
            <TextField
                value={props.state.filter.complaintRef}
                onChange={(e) => props.setter({ complaintRef: e.target.value })}
                key="enf-notice-complaintRef-key"
                size="small"
                id="notice-complaintRef"
                label="Compliance Complaint Reference"
                variant="outlined"
                className="min-width-18rem"
            />,
            <Autocomplete
                options={props.state.noticeTypes || []}
                id="notice-noticeType"
                className="search-form-autocomplete-field autocomplete-width-18rem"
                key="enf-notice-noticeType-key"
                size="small"
                inputValue={props.state.filter.noticeType}
                value={props.state.filter.noticeType?.length ? props.state.filter.noticeType : null}
                onInputChange={(event, value) => event && props.setter({ noticeType: value })}
                renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Notice Type")}
            />,
            <Autocomplete
                options={Object.keys(NoticeStatus)}
                id="notice-noticeStatus"
                className="search-form-autocomplete-field autocomplete-width-18rem"
                key="enf-notice-noticeStatus-key"
                size="small"
                inputValue={props.state.filter.noticeStatus}
                value={props.state.filter.noticeStatus?.length ? props.state.filter.noticeStatus : null}
                onInputChange={(event, value) => event && props.setter({ noticeStatus: value })}
                renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Notice Status")}
            />,
            <Autocomplete
                options={props.state.wards || []}
                inputValue={props.state.filter.noticeWard}
                value={props.state.filter.noticeWard?.length ? props.state.filter.noticeWard : null}
                onInputChange={(event, value) => event && props.setter({ noticeWard: value })}
                key="enf-notice-noticeWard-key"
                className="search-form-autocomplete-field autocomplete-width-18rem"
                size="small"
                id="notice-noticeWard"
                renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Notice Ward")}
            />,
            <Autocomplete
                options={props.state.parishes || []}
                inputValue={props.state.filter.noticeParish}
                value={props.state.filter.noticeParish?.length ? props.state.filter.noticeParish : null}
                onInputChange={(event, value) => event && props.setter({ noticeParish: value })}
                key="enf-notice-noticeParish-key"
                className="search-form-autocomplete-field autocomplete-width-18rem"
                size="small"
                id="notice-noticeParish"
                renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Notice Parish")}
            />,
        ];
    }

    function getDateFields() {
        return [
            <LocalizationProvider dateAdapter={AdapterDayjs} key="notice-Compliance-Deadline-From-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.complianceDeadlineDateFrom)}
                    onChange={(value: Dayjs | null) => props.setter({ complianceDeadlineDateFrom: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("notice-Compliance-Deadline-From") } }}
                    label="Compliance Deadline From"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="notice-Compliance-Deadline-To-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.complianceDeadlineDateTo)}
                    onChange={(value: Dayjs | null) => props.setter({ complianceDeadlineDateTo: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("notice-Compliance-Deadline-To") } }}
                    label="Compliance Deadline To"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="notice-Created-Date-From-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.noticeCreatedDateFrom)}
                    onChange={(value: Dayjs | null) => props.setter({ noticeCreatedDateFrom: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("notice-Created-Deadline-From") } }}
                    label="Notice Created Date From"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="notice-Created-Date-To-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.noticeCreatedDateTo)}
                    onChange={(value: Dayjs | null) => props.setter({ noticeCreatedDateTo: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("notice-Created-Deadline-To") } }}
                    label="Notice Created Date To"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="notice-Authorised-Date-From-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.noticeAuthorisedDateFrom)}
                    onChange={(value: Dayjs | null) => props.setter({ noticeAuthorisedDateFrom: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("notice-Authorised-Date-From") } }}
                    label="Notice Authorised Date From"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="notice-Authorised-Date-To-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.noticeAuthorisedDateTo)}
                    onChange={(value: Dayjs | null) => props.setter({ noticeAuthorisedDateTo: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("notice-Authorised-Date-To") } }}
                    label="Notice Authorised Date To"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="notice-Served-Date-From-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.noticeServedDateFrom)}
                    onChange={(value: Dayjs | null) => props.setter({ noticeServedDateFrom: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("notice-Served-Date-From") } }}
                    label="Notice Served Date From"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="notice-Served-Date-To-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.noticeServedDateTo)}
                    onChange={(value: Dayjs | null) => props.setter({ noticeServedDateTo: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("notice-Served-Date-To") } }}
                    label="Notice Served Date To"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
        ];
    }
};

export default EnforcementNoticeAdvancedSearch;
